import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, Download, Search, Shield } from 'lucide-react';
import { GovernanceBreadcrumbs, RiskBadge, btnPrimary, btnSecondary, inputClass, selectClass } from '../components/shared';
import { aiGovernanceService, LibraryTemplate, PolicyListItem } from '../../../../components/api/aiGovernanceService';

export default function PolicyLibraryScreen() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [templates, setTemplates] = useState<LibraryTemplate[]>([]);
  const [installed, setInstalled] = useState<PolicyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importingId, setImportingId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tpl, policies] = await Promise.all([
        aiGovernanceService.getTemplates({
          search: search || undefined,
          category: categoryFilter !== 'All' ? categoryFilter : undefined,
        }),
        aiGovernanceService.getPolicies({ page: 1, pageSize: 50 }),
      ]);
      setTemplates(Array.isArray(tpl) ? tpl : []);
      setInstalled(policies.items || []);
    } catch {
      setError('Failed to load policy library.');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const categories = Array.from(new Set(templates.map((t) => t.category)));

  const handleImport = async (id: string) => {
    setImportingId(id);
    setError(null);
    try {
      await aiGovernanceService.importTemplate(id, { importAll: true, status: 'Draft' });
      await fetchData();
    } catch {
      setError('Failed to import template.');
    } finally {
      setImportingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <GovernanceBreadcrumbs items={[{ label: 'AI Governance' }, { label: 'Policy Library' }]} />

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Policy Library</h1>
          <p className="text-xs text-slate-500 mt-0.5">Browse pre-built policy templates for common compliance frameworks and use cases.</p>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>}

      <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search templates..." className={`${inputClass} pl-8`} />
        </div>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className={`${selectClass} w-44`}>
          <option value="All">All Categories</option>
          {categories.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading templates...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map((template) => (
            <div key={template.id} className="bg-white border border-slate-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="px-4 py-3">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                    <BookOpen className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-slate-800">{template.name}</h3>
                      <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded font-medium">{template.category}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">{template.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-[10px] text-slate-400">
                      <span>{template.policies} policies</span>
                      <span>{template.downloads.toLocaleString()} imports</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-4 py-2 border-t border-slate-100 flex items-center gap-2">
                <button
                  onClick={() => handleImport(template.id)}
                  disabled={importingId === template.id}
                  className={btnPrimary}
                >
                  <Download className="w-3 h-3" /> {importingId === template.id ? 'Importing...' : 'Import Template'}
                </button>
                <button className={btnSecondary}>Preview</button>
              </div>
            </div>
          ))}
          {templates.length === 0 && <p className="text-sm text-slate-500">No templates found.</p>}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-4 h-4 text-indigo-600" />
          <h3 className="text-sm font-semibold text-slate-800">Installed Policies Reference</h3>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {installed.length === 0 ? (
            <span className="text-[11px] text-slate-500">No installed policies yet.</span>
          ) : installed.map((p) => (
            <span key={p.id} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-[10px] text-slate-600">
              {p.name}
              <RiskBadge level={p.protectionLevel} />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
